
import { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { APPLICATIONS_URL } from '../constants';
import 'react-toastify/dist/ReactToastify.css';

export default function Submissions() {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Check user email access
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));

    if (!storedUser || storedUser.email !== 'leakyrelu27@gmail.com') {
      toast.error('Unauthorized Access');
      navigate('/');
    }
  }, [navigate]);

  const fetchApplications = async (pageNumber = 1) => {
    setLoading(true);
    try {
      const res = await fetch(`${APPLICATIONS_URL}?page=${pageNumber}&limit=10`);
      const data = await res.json();

      if (!res.ok) {
        toast.error(data.message || 'Something went wrong');
        return;
      }

      const sortedApplications = (data.applications || []).sort((a, b) =>
        new Date(b.createdAt) - new Date(a.createdAt)
      );

      setApplications(sortedApplications);
      setFilteredApplications(sortedApplications);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Network or server error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications(page);
  }, [page]);

  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const handleSearch = (e) => {
    const keyword = e.target.value.toLowerCase();
    setSearchQuery(keyword);

    if (!keyword) {
      setFilteredApplications(applications);
    } else {
      const filtered = applications.filter((app) =>
        app.personalInfo?.email?.toLowerCase().includes(keyword)
      );
      setFilteredApplications(filtered);
    }
  };

  return (
    <div className="min-h-screen bg-white p-6">
      <ToastContainer position="top-right" />

      <div className="max-w-5xl mx-auto">
        <h2 className="text-2xl font-semibold text-center mb-6">Submitted Applications</h2>

        {/* Search Input */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search by email..."
            value={searchQuery}
            onChange={handleSearch}
            className="w-full px-4 py-2 border rounded-full bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {loading ? (
          <p className="text-center text-gray-500">Loading...</p>
        ) : (
          <div className="space-y-8">
            {filteredApplications.map((app) => (
              <div key={app.id} className="border rounded-xl p-6 shadow-sm hover:shadow-md transition space-y-3">
                <div>
                  <h3 className="font-bold text-lg">{app.personalInfo?.fullName || 'No Name Provided'}</h3>
                  <p className="text-gray-500 text-sm">Contact: {app.personalInfo?.contact || 'N/A'}</p>
                  {app.personalInfo?.email && (
                    <p className="text-gray-500 text-sm">Email: {app.personalInfo.email}</p>
                  )}
                  <p className="text-gray-400 text-xs">Submitted on: {new Date(app.createdAt).toLocaleDateString()}</p>
                </div>

                {/* Education */}
                {app.educationList && app.educationList.length > 0 && (
                  <div className="mt-2">
                    <h4 className="font-semibold text-md mb-2">Education:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {app.educationList.map((edu, eduIndex) => (
                        <li key={eduIndex} className="text-sm text-gray-700">
                          {edu.institution && <span className="font-medium">{edu.institution}</span>}{' '}
                          {edu.degree && `- ${edu.degree}`}{' '}
                          {edu.studentNumber && `(Student No: ${edu.studentNumber})`}{' '}
                          {edu.certNumber && `(Cert No: ${edu.certNumber})`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Licenses */}
                {app.licenses && app.licenses.length > 0 && (
                  <div className="mt-2">
                    <h4 className="font-semibold text-md mb-2">Licenses:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {app.licenses.map((lic, licIndex) => (
                        <li key={licIndex} className="text-sm text-gray-700">
                          {lic.title && <span className="font-medium">{lic.title}</span>}{' '}
                          {lic.number && `(No: ${lic.number})`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        <div className="flex justify-center items-center mt-8 space-x-4">
          <button
            onClick={handlePrevPage}
            disabled={page === 1}
            className="px-4 py-2 bg-primary text-white rounded-full disabled:bg-gray-300"
          >
            Previous
          </button>
          <span className="text-gray-700">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={page === totalPages}
            className="px-4 py-2 bg-primary text-white rounded-full disabled:bg-gray-300"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
